import {
    createContext,
    PropsWithChildren,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';

export const TITLE_SEPARATOR = ' | ';

/**
 * Joins title parts broadest first, like a breadcrumb, skipping blanks and a part that
 * repeats the one before it, e.g. ('Admin', 'Approvals', 'Approvals') gives
 * "Admin | Approvals". AppBranding puts the app short name in front.
 */
export const joinTitle = (...parts: (string | null | undefined)[]) =>
    parts
        .map(part => part?.trim())
        .filter((part, i, all): part is string => !!part && part !== all[i - 1])
        .join(TITLE_SEPARATOR);

interface PageTitleRegistry {
    set(id: symbol, title: string): void;
    remove(id: symbol): void;
}

const PageTitleRegistryContext = createContext<PageTitleRegistry | null>(null);
const CurrentPageTitleContext = createContext('');

/**
 * Tracks every mounted PageTitle in the order it was committed; the latest one with a
 * non-empty title wins. Registration happens in effects, never during render: react-router
 * navigates inside transitions and lazy pages suspend, so React routinely renders a page and
 * then discards it. A registry fed from render (react-helmet-async on React 18) leaks those
 * discarded titles, and the leaked title then overrides every later page.
 */
export const PageTitleProvider = ({children}: PropsWithChildren) => {
    // Map keeps insertion order, and updating an existing entry keeps its place, so a parent
    // whose title arrives late (e.g. the assistant name) never jumps ahead of a deeper page.
    const entries = useRef(new Map<symbol, string>());
    const [current, setCurrent] = useState('');

    const registry = useMemo<PageTitleRegistry>(() => {
        const publish = () => {
            const titles = [...entries.current.values()].filter(Boolean);
            setCurrent(titles[titles.length - 1] ?? '');
        };
        return {
            set: (id, title) => {
                entries.current.set(id, title);
                publish();
            },
            remove: id => {
                entries.current.delete(id);
                publish();
            }
        };
    }, []);

    return (
        <PageTitleRegistryContext.Provider value={registry}>
            <CurrentPageTitleContext.Provider value={current}>
                {children}
            </CurrentPageTitleContext.Provider>
        </PageTitleRegistryContext.Provider>
    );
};

/** The winning page title (without the app short name), or '' when none is mounted. */
export const useCurrentPageTitle = () => useContext(CurrentPageTitleContext);

/**
 * Sets the browser tab title while mounted. Place it before the page's content so a deeper
 * page's PageTitle commits after it and wins. With none mounted the tab shows just the app
 * short name. Always registers, even while the title is still loading, so it keeps its place.
 */
const PageTitle = ({title}: {title?: string | null}) => {
    const registry = useContext(PageTitleRegistryContext);
    const [id] = useState(() => Symbol('PageTitle'));
    const trimmed = title?.trim() ?? '';

    useEffect(() => {
        registry?.set(id, trimmed);
    }, [registry, id, trimmed]);

    useEffect(() => () => registry?.remove(id), [registry, id]);

    return null;
};

export default PageTitle;
